import { createFileRoute, notFound } from '@tanstack/react-router'
import { getTranslations } from '@/i18n/translator'
import { pageHead, SITE_URL } from '@/lib/seo'
import { breadcrumbSchema, homeBreadcrumbItem } from '@/lib/structured-data'
import { isPropertyFeatureKey } from '@/features/solution-page/data/features'
import { FeatureHero } from '@/features/solution-page/components/feature-hero'
import { FeatureVideo } from '@/features/solution-page/components/feature-video'
import { HowItWorks } from '@/features/solution-page/components/how-it-works-section'
import { FeatureScoreBand } from '@/features/solution-page/components/property-experts-section'
import { ClientResultsSection } from '@/features/solution-page/components/client-results-section'
import { FaqSection } from '@/features/landing-page/components/faq-section'
import { SimulatorSection } from '@/features/solution-page/components/simulator-section'
import { FinalCtaSection } from '@/features/landing-page/components/final-cta-section'

// Same five uniform FAQ item keys as people; per-feature copy lives in messages.
const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5'] as const

export const Route = createFileRoute(
  '/$locale/solutions/property-security/$feature',
)({
  loader: async ({ params }) => {
    if (!isPropertyFeatureKey(params.feature)) throw notFound()
    const t = await getTranslations({
      locale: params.locale,
      namespace: `solutions.property.features.${params.feature}`,
    })
    return {
      feature: params.feature,
      title: t('metaTitle'),
      description: t('metaDescription'),
    }
  },
  head: ({ loaderData, params }) => {
    const path = `/solutions/property-security/${params.feature}`
    return pageHead({
      locale: params.locale,
      path,
      title: loaderData?.title ?? '',
      description: loaderData?.description ?? '',
      jsonLd: breadcrumbSchema([
        homeBreadcrumbItem(params.locale),
        { name: loaderData?.title ?? '', url: `${SITE_URL}/${params.locale}${path}` },
      ]),
    })
  },
  component: FeaturePage,
})

function FeaturePage() {
  const { feature } = Route.useLoaderData()
  return (
    <>
      <FeatureHero feature={feature} category="property" />
      <FeatureVideo feature={feature} category="property" />
      <HowItWorks feature={feature} category="property" />
      <FeatureScoreBand feature={feature} />
      <ClientResultsSection feature={feature} category="property" />
      <FaqSection
        namespace={`solutions.property.features.${feature}.faq`}
        items={FAQ_KEYS}
      />
      <FinalCtaSection />
    </>
  )
}
